import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Sparkles, Check } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";

/**
 * TemplatePicker — compact "Templates ▾" button that lives beside any
 * SMS/Email composer. When a template is picked we POST to
 * /comms/templates/:id/render with any available context (customer_id,
 * job_id, invoice_id, estimate_id) so merge fields resolve against the
 * current record. The caller receives `{ subject, body }` via `onPick`
 * and decides how to apply them.
 */
export default function TemplatePicker({
  channel = "sms",             // "sms" | "email"
  customerId = null,
  jobId = null,
  invoiceId = null,
  estimateId = null,
  onPick,                       // ({subject, body}) => void
  size = "sm",                  // "sm" | "md"
  label = "Templates",
  testIdPrefix = "template-picker",
}) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/comms/templates?channel=${channel}`);
      setRows(data?.templates || []);
    } catch { setRows([]); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && rows.length === 0 && !loading) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      r.name.toLowerCase().includes(q) || r.body.toLowerCase().includes(q)
    );
  }, [rows, filter]);

  const pick = async (t) => {
    setBusyId(t.id);
    try {
      const { data } = await api.post(`/comms/templates/${t.id}/render`, {
        customer_id: customerId || null,
        job_id: jobId || null,
        invoice_id: invoiceId || null,
        estimate_id: estimateId || null,
      });
      onPick && onPick({ subject: data.subject || "", body: data.body || "" });
      setOpen(false);
    } catch {
      // Fallback: use raw template if render failed
      onPick && onPick({ subject: t.subject || "", body: t.body || "" });
      setOpen(false);
    } finally { setBusyId(""); }
  };

  const btnCls = size === "sm"
    ? "h-8 px-2.5 text-xs"
    : "h-10 px-3 text-sm";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={`${btnCls} border-zinc-200 hover:border-[#FF5A00] hover:text-[#FF5A00] gap-1 shrink-0`}
          data-testid={`${testIdPrefix}-trigger`}
        >
          <Sparkles className="w-3.5 h-3.5"/>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="p-0 w-80" data-testid={`${testIdPrefix}-content`}>
        <div className="p-2 border-b border-zinc-100">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search templates…"
            className="w-full text-xs px-2 py-1.5 rounded border border-zinc-200 focus:outline-none focus:border-[#FF5A00]"
            data-testid={`${testIdPrefix}-search`}
          />
        </div>
        <div className="max-h-64 overflow-y-auto">
          {loading ? (
            <div className="p-4 text-xs text-zinc-500 text-center">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="p-4 text-xs text-zinc-500 text-center" data-testid={`${testIdPrefix}-empty`}>
              {rows.length === 0 ? "No templates yet. Add some in Settings." : "No matches."}
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {filtered.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => pick(t)}
                    disabled={busyId === t.id}
                    className="w-full text-left px-3 py-2 hover:bg-orange-50 transition-colors group disabled:opacity-50"
                    data-testid={`${testIdPrefix}-item-${t.id}`}
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs font-medium text-zinc-900 group-hover:text-[#FF5A00] truncate">{t.name}</span>
                      {busyId === t.id && <Check className="w-3 h-3 text-emerald-500"/>}
                    </div>
                    <div className="text-[11px] text-zinc-500 truncate mt-0.5">{t.body}</div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="p-2 border-t border-zinc-100 text-[10px] text-zinc-500 font-mono-accent uppercase tracking-wider text-center">
          Merge fields are resolved on pick
        </div>
      </PopoverContent>
    </Popover>
  );
}
